import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CircleIcon from "@mui/icons-material/Circle";
import { appThemeColors, appThemeScrollbar } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";

const uiFont = appThemeFonts.family.ui;

export const hierarchyIconColor = appThemeColors.text.faint;

export const HIERARCHY_LEADING_MIN_WIDTH = 28;

export const hierarchyIconRules = {
  "&& .MuiSvgIcon-root": {
    fontSize: `${appThemeFonts.size.lg} !important`,
    width: `${appThemeFonts.size.lg} !important`,
    height: `${appThemeFonts.size.lg} !important`,
    color: `${hierarchyIconColor} !important`,
  },
  "&& svg": {
    fontSize: `${appThemeFonts.size.lg} !important`,
    width: `${appThemeFonts.size.lg} !important`,
    height: `${appThemeFonts.size.lg} !important`,
    color: `${hierarchyIconColor} !important`,
  },
};

export const hierarchyDropdownButtonStyles = {
  outline: "none",
  color: hierarchyIconColor,
  background: "none",
  border: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  flexShrink: 0,
  padding: "6px 4px",
  minWidth: HIERARCHY_LEADING_MIN_WIDTH,
  width: HIERARCHY_LEADING_MIN_WIDTH,
  maxWidth: HIERARCHY_LEADING_MIN_WIDTH,
  minHeight: "unset",
  lineHeight: 1,
  textTransform: "none",
  ...hierarchyIconRules,
  "&:hover": {
    background: "none",
  },
};

export const hierarchyCircleIconStyles = {
  boxSizing: "border-box",
  fontSize: appThemeFonts.size.xxs,
  width: appThemeFonts.size.xxs,
  height: appThemeFonts.size.xxs,
  minWidth: `${HIERARCHY_LEADING_MIN_WIDTH}px`,
  paddingLeft: "6px",
  paddingRight: "4px",
  color: hierarchyIconColor,
  flexShrink: 0,
  "& svg": {
    fill: "currentColor",
  },
};

export const HierarchyDropdownButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$selected",
})(() => hierarchyDropdownButtonStyles);

export const HierarchyCircleIcon = styled(CircleIcon, {
  shouldForwardProp: (prop) => prop !== "$selected",
})(() => hierarchyCircleIconStyles);

export const HierarchyRoot = styled(Box)({
  display: "block",
});

export const HierarchyRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$selected",
})(({ $selected }) => ({
  ...($selected && {
    cursor: "pointer",
  }),
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  whiteSpace: "nowrap",
  marginBottom: "4px",
  "&:hover": {
    textDecoration: "underline",
    textDecorationColor: "#fff",
  },
  ...($selected && {
    backgroundColor: appThemeColors.background.selected,
    borderRadius: "4px",
  }),
}));

export const HierarchyTitleButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$selected",
})(({ $selected }) => ({
  outline: "none",
  color: appThemeColors.text.primary,
  background: "none",
  border: "none",
  fontFamily: uiFont,
  display: "flex",
  alignItems: "center",
  padding: "2px 8px 2px 4px",
  fontSize: appThemeFonts.size.mdPlus,
  fontWeight: 400,
  flex: 1,
  justifyContent: "flex-start",
  textTransform: "none",
  minWidth: "unset",
  "&:hover": {
    textDecoration: "underline",
    textDecorationColor: "#fff",
  },
  ...($selected && {
    color: appThemeColors.text.heading,
    fontWeight: 600,
    backgroundColor: appThemeColors.background.selected,
  }),
  ...(!$selected && {
    color: appThemeColors.text.primary,
  }),
}));

export const HierarchyChildren = styled(Box)({
  marginLeft: "15px",
});

export const hierarchyPanelChrome = {
  boxSizing: "border-box",
  background: appThemeColors.background.paper,
  border: `1px solid ${appThemeColors.border.default}`,
  borderRadius: "14px",
  overflow: "hidden",
  boxShadow: "rgba(16, 42, 58, 0.04) 0px 1px 2px",
};

export const MainLayoutBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  height: "100%",
  overflow: "hidden",
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    overflow: "auto",
    height: "auto",
  },
}));

export const MainFlexContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  width: "100%",
  overflow: "hidden",
  alignItems: "stretch",
  gap: "16px",
  marginTop: "8px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    flex: "none",
    minHeight: "auto",
    overflow: "visible",
  },
}));

export const SideNavPanel = styled(Box)(({ theme }) => ({
  flex: "1 1 0",
  minWidth: 260,
  maxWidth: "34%",
  minHeight: 0,
  alignSelf: "stretch",
  display: "flex",
  flexDirection: "column",
  ...hierarchyPanelChrome,
  [theme.breakpoints.down("md")]: {
    flex: "none",
    maxWidth: "100%",
    minHeight: "auto",
  },
}));

export const SideNavDrawer = styled(Drawer)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  "& .MuiDrawer-paper": {
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    minHeight: 0,
    boxSizing: "border-box",
    backgroundColor: appThemeColors.background.paper,
    overflow: "hidden",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    wordBreak: "break-word",
    borderRight: 'none !important'
  },
  [theme.breakpoints.down("md")]: {
    flex: "none",
    minHeight: "auto",
    "& .MuiDrawer-paper": {
      position: "static",
      height: "auto",
      minHeight: "auto",
      overflow: "visible",
      overflowX: "visible",
    },
    "& .MuiDrawer-paper > *, & .MuiDrawer-paper > * > *": {
      height: "auto",
      minHeight: "auto",
      overflow: "visible",
      flex: "none",
    },
  },
}));

export const MainContentWrapper = styled(Box)(({ theme }) => ({
  flex: "2 1 0",
  minWidth: 0,
  minHeight: 0,
  alignSelf: "stretch",
  width: "100%",
  overflow: "auto",
  p: 3,
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  ...appThemeScrollbar,
  [theme.breakpoints.down("md")]: {
    flex: "none",
    minHeight: "auto",
    overflow: "visible",
  },
}));

export const DataManagementPageTitle = styled(Typography)({
  fontFamily: uiFont,
  fontWeight: 600,
  fontSize: appThemeFonts.size.xl,
  color: appThemeColors.text.heading,
  flexShrink: 0,
});

export const DataManagementPageSubLabel = styled("span")({
  color: appThemeColors.text.muted,
  fontWeight: 500
});

export const DataManagementSectionHeading = styled(Typography)({
  fontFamily: uiFont,
  fontWeight: 600,
  fontSize: appThemeFonts.size.lg,
  color: appThemeColors.text.heading,
});

export const DataManagementSectionHeaderRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 16px",
  borderBottom: `1px solid ${appThemeColors.background.soft}`,
});

export const DataManagementDetailPaper = styled(Paper)({
  ...hierarchyPanelChrome,
  overflow: "visible",
  flexShrink: 0,
});
